import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  ListItemButton,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  subscribeToNotifications,
} from '@/services/notificationPreferences.service';
import type { Notification } from '@/types/notifications';
import { getNotificationUrl, isExternalUrl } from '@/lib/notificationRoutes';
import { isSafeAttachmentUrl } from '@/utils/isSafeAttachmentUrl';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useWebPushSubscription } from '@/hooks/useWebPushSubscription';

const PUSH_HINT_DISMISSED_KEY = 'web-push-bell-hint-dismissed';

function readPushHintDismissed(): boolean {
  try {
    return window.localStorage.getItem(PUSH_HINT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function storePushHintDismissed() {
  try {
    window.localStorage.setItem(PUSH_HINT_DISMISSED_KEY, '1');
  } catch {
    // Storage blocked: the hint stays hidden until the next page load.
  }
}

export interface NotificationCenterProps {
  onViewAll?: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ onViewAll }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Always mounted for a signed-in user, so this instance also runs the
  // once-per-load heartbeat for a browser that is already switched on.
  const { state: webPushState } = useWebPushSubscription({ heartbeat: true, countOtherBrowsers: false });
  const [pushHintDismissed, setPushHintDismissed] = useState(readPushHintDismissed);
  const showPushHint = webPushState === 'off' && !pushHintDismissed;

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getUserNotifications({
      limit: 10,
      unreadOnly: false,
    });

    if (fetchError) {
      setError(fetchError.message);
    } else if (data) {
      setNotifications(data);
    }

    setLoading(false);
  }, []);

  const loadUnreadCount = useCallback(async () => {
    const { count } = await getUnreadNotificationCount();
    setUnreadCount(count);
  }, []);

  // Load notifications and unread count
  useEffect(() => {
    const initialize = async () => {
      await Promise.all([loadNotifications(), loadUnreadCount()]);
    };

    void initialize();

    // Subscribe to real-time updates
    const userId = user?.id;
    if (!userId) return;

    const subscription = subscribeToNotifications(userId, (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadNotifications, loadUnreadCount, user?.id]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismissPushHint = () => {
    storePushHintDismissed();
    setPushHintDismissed(true);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markNotificationAsRead(notification.id);
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
    }

    // Navigate using the URL utility (handles action_url and fallbacks)
    const url = getNotificationUrl(notification);
    handleClose();
    if (isExternalUrl(url)) {
      if (isSafeAttachmentUrl(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else {
      navigate(url);
    }
  };

  const handleMarkAllAsRead = async () => {
    const { success } = await markAllNotificationsAsRead();

    if (success) {
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    }
  };

  const handleViewAll = () => {
    handleClose();
    if (onViewAll) {
      onViewAll();
    } else {
      navigate('/notifications');
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" aria-describedby={id} aria-label="Notifications" onClick={handleClick}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { width: 400, maxWidth: '100%', maxHeight: 600 }
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <>
                <Chip label={`${unreadCount} new`} color="error" size="small" />
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={handleMarkAllAsRead} aria-label="Mark all as read">
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
        <Divider />

        {showPushHint && (
          <>
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Get these on your desktop:{' '}
                <Link component={RouterLink} to="/settings/profile#notifications" onClick={handleClose}>
                  turn on browser notifications
                </Link>
              </Typography>
              <IconButton size="small" onClick={handleDismissPushHint} aria-label="Dismiss browser notifications tip">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider />
          </>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <ListItemButton
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.is_read ? 'transparent' : 'action.selected',
                    borderLeft: notification.is_read ? 'none' : '4px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        component="span"
                        display="block"
                        variant="body2"
                        fontWeight={notification.is_read ? 'normal' : 'bold'}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }} component="span" display="block">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span" display="block">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                          })}
                        </Typography>
                      </Box>
                    }
                  />
                  {!notification.is_read && (
                    <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No notifications"
                  secondary="You're all caught up!"
                  sx={{ textAlign: 'center' }}
                />
              </ListItem>
            )}
          </List>
        )}

        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button onClick={handleViewAll} size="small" fullWidth>
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default NotificationCenter;

